import { ApiPaths } from '../Config/ApiPath';

export const DEFAULT_REGISTRATION_SETTINGS = {
  usernameMode: 'automatic',
  usernameRequired: false,
  passwordMode: 'manual',
  passwordAutoGenerated: false,
  isMobileRequired: false,
  isEmailRequired: false,
  isPasswordRequired: true,
  isSponsorRequired: true,
  isSponsorActiveRequired: true,
  isCountryCodeRequired: false,
  passwordStrength: 'basic',
};

/**
 * GET /user/registration-settings — public form config.
 */
export async function fetchRegistrationSettings(AxiosGet) {
  const res = await AxiosGet(ApiPaths.registrationSettings);
  const settings = res?.settings ?? res?.data?.settings;
  if (!settings || typeof settings !== 'object') {
    throw new Error('Invalid registration settings response');
  }
  return { ...DEFAULT_REGISTRATION_SETTINGS, ...settings };
}

/**
 * POST /user/verify-username — registration availability (public).
 */
export async function verifyUsernameAvailable(AxiosPost, username) {
  const trimmed = String(username || '').trim();
  if (!trimmed) {
    return { available: false, message: 'Username is required.' };
  }

  try {
    const res = await AxiosPost(ApiPaths.verifyUsername, { username: trimmed });
    const data = res?.data ?? res;
    return {
      available: data.available === true,
      message: data.message || (data.available ? 'Username is available.' : 'Username not available'),
      username: data.username || trimmed,
    };
  } catch (err) {
    const data = err?.response?.data ?? err;
    return {
      available: false,
      message: data?.message || 'Username not available',
      code: data?.code ?? err?.response?.status,
    };
  }
}

/** True when the server (not the user) picks the username. */
export function isAutomaticUsernameMode(settings = DEFAULT_REGISTRATION_SETTINGS) {
  return settings?.usernameMode === 'automatic' || settings?.usernameRequired === false;
}

function isUsernameCollision(error) {
  const data = error?.response?.data;
  const code = data?.code ?? error?.response?.status;
  return code === 409 || /already exists/i.test(data?.message || '');
}

/** How many times a server-side username collision is retried silently. */
export const MAX_USERNAME_COLLISION_RETRIES = 3;

/**
 * POST /user/register — body omits username in automatic mode.
 *
 * In automatic mode the server generates a random username and does NOT
 * retry on a collision, so a small share of registrations fail with
 * "Username already exists" for a username the user never chose and cannot
 * change. Re-sending the identical request gets a fresh random number, so
 * that error is retried here instead of being shown. In manual mode the
 * same error is genuine and is surfaced immediately.
 */
export async function registerUser(AxiosPost, form, settings = DEFAULT_REGISTRATION_SETTINGS) {
  const body = {
    name: String(form.name || '').trim(),
  };

  if (form.password) body.password = form.password;

  if (settings.usernameRequired && form.username?.trim()) {
    body.username = form.username.trim();
  }

  if (form.sponsor?.trim()) body.sponsor = form.sponsor.trim();
  if (form.email?.trim()) body.email = form.email.trim().toLowerCase();
  if (form.mobile?.trim()) body.mobile = form.mobile.trim();
  if (settings.isCountryCodeRequired && form.country_code) {
    body.country_code = form.country_code;
  }

  const automatic = isAutomaticUsernameMode(settings);

  for (let attempt = 0; ; attempt += 1) {
    try {
      const res = await AxiosPost(ApiPaths.register, body);
      return res?.data ?? res;
    } catch (error) {
      const retryable =
        automatic &&
        isUsernameCollision(error) &&
        attempt < MAX_USERNAME_COLLISION_RETRIES;
      if (!retryable) throw error;
    }
  }
}

/**
 * POST /user/verify-sponsor — sponsor lookup for registration (public).
 */
export async function verifySponsorValid(AxiosPost, sponsor) {
  const trimmed = String(sponsor || '').trim();
  if (!trimmed) {
    return { valid: false, message: 'Sponsor username is required.' };
  }

  try {
    const res = await AxiosPost(ApiPaths.verifySponsor, { sponsor: trimmed });
    const data = res?.data ?? res;
    const valid = data.valid === true || (data.status === true && data.valid !== false);
    return {
      valid,
      name: data.name,
      sponsor_Id: data.sponsor_Id,
      isActive: data.isActive,
      message: data.message,
    };
  } catch (err) {
    const data = err?.response?.data ?? err;
    return {
      valid: false,
      message: data?.message || 'Invalid sponsor',
      code: data?.code ?? err?.response?.status,
    };
  }
}
